import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 丙烷脱氢（PDH）P&ID 详细图：丙烷加热 → 移动床反应器 + 连续再生 → 急冷压缩 → 选择加氢 → 丙烯精馏 */
const media = {
  oil: { label: '丙烷 / 丙烯', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  h2: { label: '氢气', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  cat: { label: '催化剂（连续再生）', light: '#8e24aa', dark: '#ce93d8', width: 3.2, dash: '12 5' },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  water: { label: '水 / 急冷水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  fuel: { label: '燃料气', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
};

const devices = [
  { id: 'v101', kind: 'hDrum' as const, x: 60, y: 189, tag: 'V-101', name: '丙烷缓冲罐' },
  { id: 'f101', kind: 'furnace' as const, x: 240, y: 146, tag: 'F-101', name: '加热炉', note: '出口 600℃' },
  { id: 'r101', kind: 'reactor' as const, x: 430, y: 128, tag: 'R-101', name: '脱氢反应器', note: '移动床' },
  { id: 'rg101', kind: 'regenerator' as const, x: 373, y: 430, tag: 'RG-101', name: '催化剂再生器' },
  { id: 't101', kind: 'column' as const, x: 620, y: 101, tag: 'T-101', name: '急冷塔' },
  { id: 'c101', kind: 'compressor' as const, x: 780, y: 193, tag: 'C-101', name: '反应气压缩机' },
  { id: 'r102', kind: 'reactor' as const, x: 940, y: 128, tag: 'R-102', name: '选择加氢', note: '脱炔烃' },
  { id: 't102', kind: 'column' as const, x: 1100, y: 101, tag: 'T-102', name: '丙烯精馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-feed', label: '丙烷', medium: 'oil', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-fg', label: '燃料气', medium: 'fuel', x: 170, y: 648, direction: 'in' as const },
  { id: 'p-air', label: '再生空气', medium: 'water', x: 240, y: 567, direction: 'in' as const },
  { id: 'p-propylene', label: '丙烯', medium: 'oil', x: 1290, y: 611, direction: 'out' as const },
  { id: 'p-h2', label: '氢气', medium: 'h2', x: 1290, y: 52, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-feed', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'oil' },
  { id: 's03', from: 'f101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'oil' },
  { id: 's04', from: 'r101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'oil' },
  { id: 's05', from: 't101', fromSide: 'right', to: 'c101', toSide: 'left', medium: 'oil' },
  { id: 's06', from: 'c101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'oil' },
  { id: 's07', from: 'r102', fromSide: 'right', to: 't102', toSide: 'left', medium: 'oil' },
  { id: 's08', from: 't102', fromSide: 'bottom', to: 'p-propylene', toSide: 'left', medium: 'oil' },
  { id: 's09', from: 't102', fromSide: 'top', to: 'p-h2', toSide: 'left', medium: 'h2' },
  // 催化剂连续再生（CCR 式）：反应器 → 再生器 → 反应器
  { id: 's10', from: 'r101', fromSide: 'bottom', to: 'rg101', toSide: 'right', medium: 'cat' },
  { id: 's11', from: 'rg101', fromSide: 'top', to: 'r101', toSide: 'bottom', medium: 'cat' },
  { id: 's12', from: 'p-air', fromSide: 'right', to: 'rg101', toSide: 'left', medium: 'water' },
  { id: 's13', from: 'p-fg', fromSide: 'right', to: 'f101', toSide: 'bottom', medium: 'fuel' },
];

const PdhDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="PDH 装置详细流程"
    subtitle="丙烷加热炉（600℃）→ 移动床脱氢反应器（Pt-Sn 催化剂连续再生）→ 急冷压缩 → 选择加氢脱炔 → 丙烯精馏，副产氢气（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default PdhDiagram;
